'use client';

import { ResponsiveContainer, AreaChart as RAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { ChartDataPoint, RiskPreset, RiskColors } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface SavingsChartProps {
  chartData: ChartDataPoint[];
  totalSavings: number;
  totalReturn: number;
  riskInfo?: RiskPreset;
  riskColors?: RiskColors;
  translations: ToolsTranslations['savings'];
  currency: string;
}

export default function SavingsChart({ chartData, totalSavings, totalReturn, riskInfo, riskColors, translations, currency }: SavingsChartProps) {
  // Get locale based on currency
  const getLocale = (currency: string) => {
    switch (currency) {
      case '£': return 'en-GB';
      case '$': return 'en-US';
      case '€': return 'de-DE';
      case 'CHF': return 'de-CH';
      case 'kr': 
        // For 'kr' we need to determine if it's SEK, NOK, or DKK based on context
        // Since we can't distinguish between them by symbol alone, default to Swedish
        return 'sv-SE';
      default: return 'sv-SE';
    }
  };

  const locale = getLocale(currency);

  // Build dynamic y-axis label with current currency
  const yAxisBase = translations.yAxisLabel.split('(')[0].trim();
  const yAxisLabel = `${yAxisBase} (${currency})`;

  // Custom tooltip component for better styling
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = (props: any) => {
    const { payload, active } = props;
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[240px] max-w-[300px]">
        <div className="mb-3 pb-2 border-b border-gray-100">
          <div className="text-sm font-semibold text-gray-900">{data.year}</div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex-shrink-0">{translations.chartData.total}:</span>
            <span className="text-sm font-semibold text-blue-700 text-right ml-4">
              {data['Totalt'].toLocaleString(locale)} {currency}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex-shrink-0">{translations.chartData.savings}:</span>
            <span className="text-sm font-semibold text-emerald-700 text-right ml-4">
              {data['Sparande'].toLocaleString(locale)} {currency}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 flex-shrink-0">{translations.chartData.return}:</span>
            <span className="text-sm font-semibold text-green-700 text-right ml-4">
              {data['Avkastning'].toLocaleString(locale)} {currency}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations.chartTitle}</h3>
        <p className="text-sm text-gray-600">{translations.chartSubtitle}</p>
      </div>

      {/* Chart wrapper */}
      <div className="relative w-full bg-gray-50/40 rounded-xl pt-5 pb-16 mb-6">
        <span className="absolute left-0 -top-2 text-xs text-gray-500">{yAxisLabel}</span>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RAreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 28, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" interval="preserveStartEnd" tickMargin={8} />
              <YAxis hide />
              <Tooltip content={CustomTooltip} />
              <Area type="monotone" dataKey="Sparande" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
              <Area type="monotone" dataKey="Totalt" stroke="#3b82f6" fill="#3b82f622" strokeWidth={2} />
            </RAreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="bg-emerald-50/70 rounded-lg p-3 border border-emerald-100">
          <div className="text-emerald-900 font-semibold">{translations.savings}</div>
          <div className="text-emerald-700">{Math.round(totalSavings).toLocaleString(locale)} {currency}</div>
        </div>
        <div className="bg-blue-50/70 rounded-lg p-3 border border-blue-100">
          <div className="text-emerald-900 font-semibold">{translations.return}</div>
          <div className="text-blue-700">{Math.round(totalReturn).toLocaleString(locale)} {currency}</div>
        </div>
      </div>

      {/* Risk Assessment Boxes */}
      {riskInfo && riskColors && (
        <div className="grid grid-cols-2 gap-4 text-sm mt-4">
          <div className={`rounded-lg p-3 border ${riskColors.bgColor} border-${riskColors.borderColor}`}>
            <div className={`font-semibold text-${riskColors.color}-900`}>{translations.riskLevel}</div>
            <div className={`text-${riskColors.color}-700`}>{riskInfo.name}</div>
          </div>
          <div className="bg-gray-50/70 rounded-lg p-3 border border-gray-100 text-sm">
            <div className="text-gray-900 font-semibold">{translations.riskProfile}</div>
            <div className="text-gray-700">{riskInfo.summary}</div>
          </div>
        </div>
      )}
    </div>
  );
}
